/**
 * `/invite/:token` — pre-sign-in invite preview (Tom F1 P0).
 *
 * Per `docs/audits/ux-log-tom-member-2026-05-01.md` F1 verbatim:
 *
 *     "Tom is being asked to authenticate to find out what he's
 *     authenticating into. … the invite email could surface team name +
 *     inviter name + role, and the `/invite/:token` page could render
 *     those pre-sign-in (read from a public `GET /api/invites/:token/
 *     preview` shape). Same backend, +1 endpoint, ~0.5d."
 *
 * Why this page exists
 * ────────────────────
 * Before Round H W2-B (`b214735`), an unauthenticated visitor to
 * `/invite/:token` was bounced straight to Clerk; Tom couldn't read
 * what he was joining without handing over an email. This page is the
 * calm interstitial that closes that 60-second-exit-risk gap. The
 * existing `<InviteAcceptPage/>` keeps its post-auth role of consuming
 * the token + minting the membership; it now lives at
 * `/invite/:token/accept` (mounted by `App.tsx`), reached via the
 * `consumePendingInviteRedirect()` round-trip after Clerk auth.
 *
 * Routing flow (the dispatch brief routing-flow §)
 * ────────────────────────────────────────────────
 *   1. User clicks email invite → lands HERE (`/invite/:token`),
 *      unauthenticated. Reads team + inviter + role pre-sign-in.
 *   2. Click "Sign up and join" → stash `{token, returnTo:
 *      /invite/:token/accept}` in sessionStorage → `<Navigate
 *      to="/sign-up"/>`.
 *   3. Post-Clerk-auth → `<RequireAuth/>` runs
 *      `consumePendingInviteRedirect()`, lands user at
 *      `/invite/:token/accept` → `InviteAcceptPage` does the POST.
 *   4. From there → `/teams/:teamId` (existing AppShell flow).
 *
 * If the user is already authenticated when they hit `/invite/:token`
 * (e.g. they have an active session and clicked a fresh link), we
 * skip the preview and redirect straight to the accept flow — the
 * preview is a trust surface for *first-time* visitors, not a friction
 * tax on members who already know the brand.
 *
 * Brand-pillar discipline (`feedback_brand_pillars.md`)
 * ─────────────────────────────────────────────────────
 * - **Trustable**: explicit team/inviter context up-front; honest
 *   "what you'll see post-sign-up" copy; calm 404 on bad tokens.
 * - **Worker-friendly**: no "tracking", "monitoring", "performance".
 *   Inviter framed as "{Inviter} invited you to join {Team}", not
 *   "{Team} has assigned you a seat".
 * - **Privacy-assurance**: `<ThreeCirclesPrivacyStrip
 *   audience="member" tone="quiet"/>` embedded as supporting context
 *   below the focal welcome — Tom reads what stays private before
 *   handing over an email.
 *
 * Focal-component discipline (`feedback_ui_section_focal_component.md`)
 * ─────────────────────────────────────────────────────────────────────
 * The focal element is the welcome copy card —
 * "{Inviter} invited you to join {Team} on Tarii." Big text, centred,
 * one beat. The privacy strip is `tone="quiet"` so it doesn't compete.
 * The CTA cluster is below, primary first.
 */
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import type { paths } from "../api/schema.gen";
import { Logo } from "../components/Logo";
import { ThreeCirclesPrivacyStrip } from "../components/ThreeCirclesPrivacyStrip";
import { Button, Card, CardContent, PageLead } from "../components/ui";
import { useAuth } from "../lib/AuthContext";

type InvitePreviewResponse =
  paths["/api/invites/{token}/preview"]["get"]["responses"]["200"]["content"]["application/json"];

const INVITE_PENDING_KEY = "roster.invite_pending";

type State =
  | { kind: "loading" }
  | { kind: "ok"; preview: InvitePreviewResponse }
  | { kind: "missing" }
  | { kind: "error"; message: string };

/**
 * Token-driven role copy. Healthcare invites at v0.1 are overwhelmingly
 * `member`; `lead` / `co_lead` invites are rare but supported. Plain
 * English on each so Tom doesn't see "co_lead" in the wild.
 */
const ROLE_COPY: Record<string, string> = {
  member: "as a team member",
  co_lead: "as a co-lead",
  lead: "as a team lead",
};

function roleCopy(role: string): string {
  return ROLE_COPY[role] ?? `as a ${role}`;
}

export function InvitePreviewPage() {
  const { token = "" } = useParams<{ token: string }>();
  const { accountId, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ kind: "loading" });

  // Already-authenticated visitors skip the preview entirely. The
  // preview's job is to land trust on a wary first-time visitor; an
  // already-signed-in member who clicks a fresh link should short-
  // circuit straight to acceptance.
  const skipPreview = !authLoading && Boolean(accountId);

  useEffect(() => {
    // Don't kick off the public preview fetch while the AuthProvider
    // is still hydrating — once it resolves, an already-signed-in
    // visitor short-circuits to the accept route and the fetch would
    // have been wasted work (and noisy in test assertions).
    if (authLoading) return;
    if (skipPreview) return;
    if (!token) {
      setState({ kind: "missing" });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await apiFetch<InvitePreviewResponse>(
          `/api/invites/${encodeURIComponent(token)}/preview`,
          { method: "GET" },
        );
        if (!cancelled) setState({ kind: "ok", preview: result });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ kind: "missing" });
        } else {
          const message =
            err instanceof ApiError
              ? err.detail
              : err instanceof Error
                ? err.message
                : "Could not load invite";
          setState({ kind: "error", message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, skipPreview, authLoading]);

  if (skipPreview) {
    // Authenticated already — straight to accept. No sessionStorage
    // round-trip needed; the accept route handles idempotency.
    return <Navigate to={`/invite/${encodeURIComponent(token)}/accept`} replace />;
  }

  function stashAndGo(target: "/sign-up" | "/sign-in") {
    // Mirror the contract `consumePendingInviteRedirect()` reads in
    // `App.tsx`. Round H W2-B (`b214735`) added the consumer; we
    // write the canonical `{token, returnTo}` pair pointing at the
    // *accept* route so post-auth lands the user on the membership-
    // creation flow, not back at the preview.
    try {
      window.sessionStorage.setItem(
        INVITE_PENDING_KEY,
        JSON.stringify({
          token,
          returnTo: `/invite/${token}/accept`,
        }),
      );
    } catch {
      // Private-mode Safari etc. — graceful degradation: the auth
      // round-trip will land the user on `/` and a manual click on
      // the email link a second time will succeed.
    }
    navigate(target);
  }

  function decline() {
    setState({ kind: "missing" });
  }

  // ─── Render branches ──────────────────────────────────────────────

  if (state.kind === "missing") {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-6 px-4 py-12 text-center">
        <Logo />
        <PageLead
          className="mb-0 items-center text-center"
          title="This invite link is no longer active"
          subtitle="The link may have expired, already been used, or never existed. If you think this is a mistake, ask your team lead to send a fresh invite."
        />
        <Button asChild variant="secondary" size="sm">
          <a href="/">Back to home</a>
        </Button>
      </main>
    );
  }

  if (state.kind === "error") {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-6 px-4 py-12 text-center">
        <Logo />
        <PageLead
          className="mb-0 items-center text-center"
          title="We couldn't load this invite"
          subtitle={state.message}
        />
        <Button asChild variant="secondary" size="sm">
          <a href="/">Back to home</a>
        </Button>
      </main>
    );
  }

  if (state.kind === "loading") {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-6 px-4 py-12 text-center">
        <Logo />
        <Loader2 className="h-8 w-8 animate-spin text-accent" aria-hidden="true" />
        <p className="text-sm text-fg-subtle">Loading your invite…</p>
      </main>
    );
  }

  const { preview } = state;
  const welcomeCopy = `${preview.inviter_name} invited you to join ${preview.team_name} on Tarii.`;
  const roleLine = `You'll join ${roleCopy(preview.role)}.`;

  return (
    <main className="mx-auto flex min-h-screen max-w-2xl flex-col items-center gap-8 px-4 py-12">
      <Logo />

      <PageLead
        className="mb-0 items-center text-center"
        eyebrow="You're invited"
        title={preview.team_name}
        subtitle={roleLine}
      />

      {/*
        Focal section per `feedback_ui_section_focal_component.md`:
        the welcome card is the single element a wary first-time
        visitor's eye should land on. Larger weight, centred, one
        sentence — everything else recedes around it.
      */}
      <Card
        className="w-full border-accent/30 bg-accent-soft/30"
        data-testid="invite-preview-focal-card"
      >
        <CardContent className="flex flex-col items-center gap-4 p-8 text-center">
          <p
            className="text-2xl font-semibold leading-snug text-fg sm:text-3xl"
            data-testid="invite-preview-welcome-copy"
          >
            {welcomeCopy}
          </p>
          <p className="max-w-prose text-sm text-fg-muted">
            Tarii is a calmer way for clinical teams to plan rotas. We help your lead build a fair
            roster, and we give you a clear view of your own hours, leave, and compliance — nothing
            more.
          </p>
          <p className="max-w-prose text-sm text-fg-muted">
            After you sign up, you'll see a calm welcome with what stays private to you, what your
            lead can see, and where to find your own records.
          </p>
        </CardContent>
      </Card>

      <ThreeCirclesPrivacyStrip audience="member" tone="quiet" className="w-full" />

      <div
        className="flex w-full flex-col items-center gap-3"
        data-testid="invite-preview-cta-cluster"
      >
        <Button
          variant="primary"
          size="lg"
          className="w-full sm:w-auto"
          onClick={() => stashAndGo("/sign-up")}
          data-testid="invite-preview-sign-up"
        >
          Sign up and join
        </Button>
        <button
          type="button"
          onClick={() => stashAndGo("/sign-in")}
          className="text-sm font-medium text-accent-fg underline-offset-4 hover:underline"
          data-testid="invite-preview-sign-in"
        >
          Already have an account? Sign in instead
        </button>
        <button
          type="button"
          onClick={decline}
          className="text-xs text-fg-subtle underline-offset-4 hover:text-fg-muted hover:underline"
          data-testid="invite-preview-decline"
        >
          Decline this invite
        </button>
      </div>
    </main>
  );
}
